import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function ParamsWiringProgress({ remote }) {
  const rows = useMemo(() => {
    if (!remote?.schema) return [];
    return Object.entries(remote.schema).map(([key, spec]) => ({
      key,
      status: spec.status || "active",
      enforced: "enforced_value" in spec,
      current: remote.params?.[key] ?? remote.defaults?.[key] ?? null,
      def: remote.defaults?.[key] ?? spec.default ?? null,
      tuned: remote.params?.[key] !== undefined &&
              JSON.stringify(remote.params[key]) !== JSON.stringify(remote.defaults?.[key] ?? spec.default),
    }));
  }, [remote]);

  const active = rows.filter((r) => r.status === "active");
  const advisory = rows.filter((r) => r.status !== "active");
  const tunable = active.filter((r) => !r.enforced);
  const total = rows.length;
  const pct = total > 0 ? Math.round((active.length / total) * 100) : 0;

  const fmt = (v) => (v === null || v === undefined
    ? "—"
    : typeof v === "object" ? JSON.stringify(v) : String(v));

  return (
    <div className="surface rounded-sm p-4" data-testid="params-wiring-progress">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
            Params Wiring Progress
          </div>
          <p className="text-xs text-slate-400 mt-1">
            ACTIVE fields are enforced by the engine. ADVISORY fields are
            accepted &amp; validated but not yet wired — safe to tune while we
            promote more of them.
          </p>
        </div>
        <div className="text-right font-mono">
          <div className="text-3xl text-emerald-400" data-testid="wiring-count">
            {active.length}
            <span className="text-slate-500 text-lg">/{total}</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {pct}% wired · {tunable.length} tunable
          </div>
        </div>
      </div>

      <div
        className="h-2 w-full bg-slate-900 rounded-sm overflow-hidden border border-slate-800"
        data-testid="wiring-progress-bar"
      >
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ParamGroup
          label={`ACTIVE (${active.length})`}
          items={active}
          accent="emerald"
          testid="wiring-active-list"
          fmt={fmt}
        />
        <ParamGroup
          label={`ADVISORY (${advisory.length})`}
          items={advisory}
          accent="amber"
          testid="wiring-advisory-list"
          fmt={fmt}
        />
      </div>
    </div>
  );
}

function ParamGroup({ label, items, accent, testid, fmt }) {
  const accentText = accent === "emerald" ? "text-emerald-400" : "text-amber-400";
  const accentBorder = accent === "emerald" ? "border-l-emerald-500" : "border-l-amber-500";
  return (
    <div className={`border-l-2 ${accentBorder} pl-3`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.2em] ${accentText} mb-2`}>
        {label}
      </div>
      <div className="max-h-64 overflow-y-auto pr-1 space-y-1">
        {items.length === 0 && (
          <div className="text-xs text-slate-600 italic">—</div>
        )}
        {items.map((row) => (
          <div
            key={row.key}
            className="flex items-baseline justify-between gap-2 border-b border-slate-800/60 py-1 text-[11px] font-mono"
            data-testid={`wiring-row-${row.key}`}
          >
            <span className="text-slate-300 truncate flex items-center gap-1">
              {row.enforced && (
                <span
                  title="Engine-enforced invariant — value is locked"
                  className="text-slate-600"
                >
                  ⚿
                </span>
              )}
              {row.key}
            </span>
            <span className="flex items-center gap-2 text-slate-500 shrink-0">
              <span className={row.tuned ? "text-blue-300" : row.enforced ? "text-slate-500" : "text-slate-400"}>
                {fmt(row.current)}
              </span>
              {row.tuned && !row.enforced && (
                <span className="text-[9px] text-slate-500">
                  (def {fmt(row.def)})
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, obj, testid }) {
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
        {Object.entries(obj || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-800 py-1.5">
            <span className="text-slate-400">{k}</span>
            <span className="text-slate-200">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StrategyEditor({ remote, onReload, onUniverseChanged }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Remember the last-submitted overrides so we can show the user EXACTLY
  // which keys their paste caused to change (the editor otherwise reloads
  // with the full effective schema and the user thinks "nothing happened").
  const [lastApplied, setLastApplied] = useState(null);
  const [lastAppliedUniverse, setLastAppliedUniverse] = useState(null);

  useEffect(() => {
    if (remote?.params) setText(JSON.stringify(remote.params, null, 2));
  }, [remote]);

  const save = async () => {
    setError("");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/strategy/params", {
        params: parsed,
        note: "ui-update",
      });
      // Capture applied fields for the confirmation banner. The backend
      // response includes `params` (normalized overrides) and optionally
      // `universe` (if the nested payload had a universe block).
      const applied = data?.params || {};
      const universe = data?.universe || null;
      const count = Object.keys(applied).length;
      setLastApplied(applied);
      setLastAppliedUniverse(universe);
      toast.success(
        `✓ Saved ${count} params${universe ? " + universe" : ""} — engine uses new values on the next signal cycle`,
      );
      onReload();
      // If the payload updated the universe (V2 nested paste), also refresh
      // the parent's Universe Editor table so the on-page state stays in
      // sync without requiring a hard reload.
      if (universe && typeof onUniverseChanged === "function") {
        onUniverseChanged();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    try {
      await api.post("/strategy/reset");
      toast("Reset to defaults");
      onReload();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const copyTunableTemplate = async () => {
    // Build a JSON containing only tunable (non-enforced) fields, seeded
    // with the current effective values so the user has a valid starting
    // template to iterate on with ChatGPT.
    const params = remote?.params || {};
    const defaultsMap = remote?.defaults || {};
    const schema = remote?.schema || {};
    const tunable = {};
    Object.keys(schema)
      .filter((k) => !("enforced_value" in schema[k]))
      .sort()
      .forEach((k) => {
        tunable[k] = params[k] !== undefined ? params[k] : defaultsMap[k];
      });
    const json = JSON.stringify(tunable, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      toast.success(`Copied ${Object.keys(tunable).length} tunable fields to clipboard`);
    } catch (e) {
      // Fallback for browsers without clipboard permission — drop into the
      // editor textarea so the user can still copy it manually.
      setText(json);
      toast(`Loaded ${Object.keys(tunable).length} tunable fields into the editor (clipboard denied)`);
    }
  };

  // Sorted chip rows — memo avoids sort() on every re-render.
  const sortedLastApplied = useMemo(
    () => (lastApplied ? Object.entries(lastApplied).sort(([a], [b]) => a.localeCompare(b)) : []),
    [lastApplied],
  );

  if (!remote) return null;

  return (
    <div className="surface rounded-sm p-4" data-testid="strategy-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
            Custom Strategy Overrides (JSON)
          </div>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Paste new params below (from ChatGPT or hand-tuning). Only whitelisted keys
            are accepted; values are validated against the schema. Changes apply to the
            next 5-min evaluation cycle — no restart required.
            <span className="text-slate-500"> Tip: hit </span>
            <span className="text-blue-300 font-mono">Copy Tunable Template</span>
            <span className="text-slate-500"> for a ready-made JSON of every field you're allowed to tweak.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="strategy-copy-template-btn"
            onClick={copyTunableTemplate}
            className="px-3 py-1.5 border border-slate-700 hover:border-blue-500 hover:bg-blue-500/10 text-[10px] uppercase tracking-[0.15em] rounded-sm text-slate-300 hover:text-blue-300"
            title="Copy all tunable (non-enforced) fields as a JSON template — perfect for iterating with ChatGPT."
          >
            Copy Tunable Template
          </button>
          <button
            data-testid="strategy-reset-btn"
            onClick={resetDefaults}
            className="px-3 py-1.5 border border-slate-700 hover:bg-slate-800/50 text-[10px] uppercase tracking-[0.15em] rounded-sm text-slate-300"
          >
            Reset to defaults
          </button>
          <button
            data-testid="strategy-save-btn"
            disabled={saving}
            onClick={save}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Apply Params"}
          </button>
        </div>
      </div>

      <textarea
        data-testid="strategy-params-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={12}
        spellCheck={false}
        className="w-full font-mono text-xs bg-slate-950 border border-slate-800 rounded-sm p-3 text-blue-100 focus:border-blue-500 outline-none"
      />

      {error && (
        <div
          data-testid="strategy-error"
          className="mt-2 text-xs font-mono text-red-400 bg-red-950/30 border border-red-900 rounded-sm p-2"
        >
          {error}
        </div>
      )}

      {lastApplied && (
        <div
          className="mt-3 border-l-2 border-l-emerald-500 bg-emerald-950/20 rounded-sm p-3"
          data-testid="strategy-last-applied"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-2">
            ✓ Last apply — {Object.keys(lastApplied).length} params saved
            {lastAppliedUniverse ? " + universe" : ""}
          </div>
          <div className="text-[11px] text-slate-300 leading-relaxed mb-2">
            The editor above now shows the <span className="text-blue-300">full effective config</span> (built-in defaults merged with your overrides). Your changes ARE saved — this is just how the engine sees it. Compare against the fields listed below to confirm your paste took effect.
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="strategy-last-applied-chips">
            {sortedLastApplied.map(([k, v]) => (
              <div
                key={k}
                className="text-[10px] font-mono border border-emerald-800 bg-emerald-900/20 rounded-sm px-2 py-0.5"
                data-testid={`strategy-applied-${k}`}
                title={`${k} = ${JSON.stringify(v)}`}
              >
                <span className="text-emerald-300">{k}</span>
                <span className="text-slate-400"> = </span>
                <span className="text-slate-200">
                  {typeof v === "string" ? v.slice(0, 32) : JSON.stringify(v)}
                </span>
              </div>
            ))}
          </div>
          {lastAppliedUniverse && (
            <div className="mt-3 pt-3 border-t border-emerald-900/50">
              <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 mb-1">
                Universe applied
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="strategy-last-applied-universe">
                {Object.entries(lastAppliedUniverse).map(([u, cfg]) => (
                  <div
                    key={u}
                    className="text-[10px] font-mono border border-slate-700 rounded-sm px-2 py-0.5"
                  >
                    <span className="text-slate-200">{u}</span>
                    <span className="text-slate-500"> → </span>
                    <span
                      className={
                        cfg.state === "enabled"
                          ? "text-emerald-300"
                          : cfg.state === "paper_only"
                          ? "text-blue-300"
                          : "text-slate-500"
                      }
                    >
                      {cfg.state}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <details className="mt-3 text-[11px]">
        <summary className="cursor-pointer text-slate-400 hover:text-slate-200 uppercase tracking-[0.15em]">
          Schema & allowed ranges
        </summary>
        <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-slate-400">
          {Object.entries(remote.schema).map(([k, s]) => (
            <div key={k} className="border border-slate-800 rounded-sm px-2 py-1">
              <span className="text-blue-300">{k}</span>{" "}
              <span className="text-slate-500">
                {s.type}
                {s.min != null ? ` [${s.min}..${s.max}]` : ""}
                {" · default "}
                <span className="text-slate-300">{String(s.default)}</span>
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

export default function Algorithm() {
  const [algo, setAlgo] = useState(null);
  const [remote, setRemote] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [universeBusy, setUniverseBusy] = useState(false);

  const loadStrategy = async () => {
    try {
      const { data } = await api.get("/strategy/params");
      setRemote(data);
    } catch (e) {
      toast.error("Failed to load strategy params");
    }
  };

  const loadUniverse = async () => {
    try {
      const { data } = await api.get("/strategy/universe");
      setUniverse(data);
    } catch (e) {
      // Endpoint may not be deployed yet on older backends — dev-only log.
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("universe load skipped:", e?.response?.status || e?.message);
      }
    }
  };

  const saveUniverseState = async (underlying, nextState) => {
    if (!universe) return;
    setUniverseBusy(true);
    try {
      const next = { ...universe.universe };
      next[underlying] = { ...(next[underlying] || {}), state: nextState };
      const { data } = await api.put("/strategy/universe", {
        universe: next,
        note: `ui: ${underlying} -> ${nextState}`,
      });
      toast.success(`${underlying}: ${nextState}`);
      // Optimistically update local state with server-normalized shape.
      setUniverse((prev) => ({ ...prev, universe: data.universe }));
      loadUniverse();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Universe update failed");
    } finally {
      setUniverseBusy(false);
    }
  };

  useEffect(() => {
    api.get("/algorithm/version").then(({ data }) => setAlgo(data));
    loadStrategy();
    loadUniverse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!algo) return <div className="text-slate-500">Loading…</div>;

  const currentStrategy = remote?.effective?.strategy_name || "NETEDGE_INDEX_MOMENTUM_V1";

  return (
    <div className="space-y-4" data-testid="algorithm-page">
      <div>
        <h1 className="text-2xl font-medium tracking-tight">Algorithm</h1>
        <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
          Version: <span className="font-mono text-slate-200">{algo.version}</span>
          <span className="ml-4">Strategy: <span className="font-mono text-blue-300" data-testid="active-strategy-name">{currentStrategy}</span></span>
        </p>
      </div>

      <ParamsWiringProgress remote={remote} />

      <StrategyEditor remote={remote} onReload={loadStrategy} onUniverseChanged={loadUniverse} />

      {/* Underlying universe (Phase 0/1 of V2) */}
      {universe && (
        <div className="surface rounded-sm p-4" data-testid="universe-editor">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center justify-between">
            <span>Underlying Universe</span>
            <span className="text-slate-600 normal-case tracking-normal text-[10px]">
              Paper: <span className="text-blue-300">{universe.active_paper.join(", ") || "—"}</span> · Live: <span className="text-emerald-300">{universe.active_live.join(", ") || "—"}</span>
            </span>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Independently enable each index. <span className="text-blue-300">paper_only</span> restricts to paper-trading mode (recommended for SENSEX until 30–60 paper sessions completed).
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="text-left py-2">Underlying</th>
                <th className="text-left py-2">Exchange</th>
                <th className="text-left py-2">Segment</th>
                <th className="text-left py-2">State</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(universe.universe).map(([u, cfg]) => (
                <tr key={u} className="border-t border-slate-800/50" data-testid={`universe-row-${u}`}>
                  <td className="py-2 font-mono text-slate-200">{u}</td>
                  <td className="py-2 text-slate-400">{cfg.exchange}</td>
                  <td className="py-2 text-slate-400">{cfg.exchange_segment}</td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      {universe.states.map((s) => (
                        <button
                          key={s}
                          disabled={universeBusy || cfg.state === s}
                          onClick={() => saveUniverseState(u, s)}
                          data-testid={`universe-${u}-${s}`}
                          className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                            cfg.state === s
                              ? "border-blue-500 bg-blue-500/20 text-blue-300"
                              : "border-slate-700 text-slate-400 hover:bg-slate-800/50"
                          } disabled:opacity-50`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Section title="Trading Window" obj={algo.trading_window} testid="algo-window" />
      <Section title="Thresholds" obj={algo.thresholds} testid="algo-thresholds" />
      <Section title="Risk Parameters" obj={algo.risk} testid="algo-risk" />

      <div className="surface rounded-sm p-4" data-testid="algo-score">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Score Formula</div>
        <pre className="text-xs text-blue-300 whitespace-pre-wrap break-words">{algo.score_formula}</pre>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-4 mb-2">Tie Break</div>
        <div className="text-xs text-slate-300 font-mono">{algo.tie_break}</div>
      </div>

      <div className="surface rounded-sm p-4 border-l-2 border-l-amber-500">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-2">Disclosure</div>
        <p className="text-xs text-slate-300 leading-relaxed">
          Trading involves risk. Paper-trading results do not guarantee live results. This algorithm is deterministic — no LLM or discretionary override is used in trading decisions. All costs shown are estimates or Kite virtual contract note reconciliations; final settled charges may vary due to contract-note rounding.
        </p>
      </div>

      <MtfConfigPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MTF Impulse mini-config panel — read/write the new /api/mtf/config endpoint.
// Kept intentionally light: shows the effective config and lets the user
// override 4-5 highest-impact knobs without curl. Full tuning happens via
// PUT /api/mtf/config (all 40+ fields) once the MTF Momentum Lab page (Phase C)
// ships. Zero and false are preserved verbatim (no `x or default`).
// ---------------------------------------------------------------------------
function MtfConfigPanel() {
  const [cfg, setCfg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [edits, setEdits] = React.useState({});
  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/mtf/config");
      setCfg(data);
      setEdits({});
    } catch (e) {
      setMsg({ level: "error", text: `Load failed: ${e?.response?.data?.detail || e.message}` });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const highlight = [
    ["adx_min", "5m ADX minimum"],
    ["volume_ratio_min", "NIFTYBEES 1m volume ratio min"],
    ["max_hold_minutes", "Max hold (minutes)"],
    ["reward_to_risk", "Reward-to-risk (R multiple)"],
    ["max_risk_rupees", "Max risk per lot (₹)"],
    ["minimum_target_cost_multiple", "Target must be Nx round-trip cost"],
  ];

  const setField = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (Object.keys(edits).length === 0) return;
    setBusy(true); setMsg(null);
    try {
      // Coerce numeric fields (defaults are numbers).
      const params = {};
      Object.entries(edits).forEach(([k, v]) => {
        if (v === "" || v === null) return;
        const dflt = cfg?.defaults?.[k];
        params[k] = typeof dflt === "number" && !Number.isNaN(Number(v)) ? Number(v) : v;
      });
      const { data } = await api.put("/mtf/config", { params });
      setMsg({ level: "ok",
                text: `Saved v${data.config_version} · ${data.config_hash}` });
      await load();
    } catch (e) {
      setMsg({ level: "error",
                text: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <div className="surface rounded-sm p-4" data-testid="mtf-panel-loading">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          MTF Impulse Config
        </div>
        <div className="text-xs text-slate-500 py-4">Loading…</div>
      </div>
    );
  }
  return (
    <div className="surface rounded-sm p-4 border-l-2 border-l-purple-500" data-testid="mtf-config-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-400">
          MTF Impulse Config
        </div>
        <div className="text-[10px] text-slate-500 font-mono">
          v{cfg.config_version || 0} · {cfg.config_hash || "defaults"}
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        <code className="text-slate-300">NIFTY_MTF_IMPULSE_CONTINUATION_V1</code> —
        long-only paper experiment. Tunes below persist to Mongo and take
        effect on the next signal cycle. Full 40+ field editor ships with
        the MTF Momentum Lab page (Phase C).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {highlight.map(([k, label]) => (
          <label key={k} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-slate-400 shrink-0">{label}</span>
            <input
              type="number"
              step="any"
              data-testid={`mtf-field-${k}`}
              defaultValue={cfg.effective?.[k] ?? ""}
              onChange={(e) => setField(k, e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-sm px-2 py-1 w-32 font-mono text-slate-200 text-right focus:outline-none focus:border-purple-500"
            />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={busy || Object.keys(edits).length === 0}
          data-testid="mtf-config-save"
          className="text-xs uppercase tracking-[0.15em] px-4 py-1.5 rounded-sm bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Saving…" : "Save MTF Params"}
        </button>
        {msg && (
          <div className={`text-xs font-mono ${
            msg.level === "ok" ? "text-pos" : "text-red-400"
          }`} data-testid="mtf-config-msg">
            {msg.text}
          </div>
        )}
      </div>
      <details className="mt-4">
        <summary className="text-[10px] uppercase tracking-[0.2em] text-slate-500 cursor-pointer hover:text-slate-300">
          View full effective config
        </summary>
        <pre className="mt-2 text-[10px] font-mono bg-slate-950 border border-slate-800 rounded-sm p-3 overflow-x-auto text-slate-400">
{JSON.stringify(cfg.effective, null, 2)}
        </pre>
      </details>
    </div>
  );
}
